import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import {
  CellClassParams,
  ColGroupDef,
  ICellRendererParams,
} from "ag-grid-community";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { FirstDataRenderedEvent, GridReadyEvent } from "ag-grid-community";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";

import { nameof } from "../../common/ObjectUtils";
import {
  IARAggregatedRow,
  IARRowsPerGroup,
  ISingleLocationAvailabilityReport,
} from "../types/ISingleLocationAvailabilityReport";

import { useTranslation } from "react-i18next";
import "./availabilityReport.css";
import { DrillDownLoader, useLazyDrillDown } from "./useLazyDrillDown";
import {
  applyFixedColumnWidths,
  fitVisibleColumnTypes,
  GROUP_COL_ID,
  newFitState,
  resetFitState,
} from "./availabilityColumnWidths";

type Props = {
  data: ISingleLocationAvailabilityReport;
  /**
   * CR LPP-20500 (drilldown scalabile) — se presente, il drill-down (carSegment → carClass →
   * ACRISS) dei custom group del DMS NON è nella response iniziale: viene caricato alla prima
   * espansione di un custom group chiamando questo loader.
   */
  onLoadDrillDown?: DrillDownLoader;
  /**
   * CR LPP-20500 — se false, il giorno corrente non fa parte del range ricercato
   * (ricerca interamente passata o futura): i gruppi colonna "oggi inizio giornata"
   * (START_TODAY) e "oggi tempo reale" (CURRENT_TODAY) non vengono mostrati.
   */
  todayInRange?: boolean;
  /** LPP-20500 (perf) — accordion aperto al primo render (default true). */
  defaultExpanded?: boolean;
};

/**
 * Riga della tabella arricchita con i metadati di drill-down (CR LPP-20500 RF-08).
 * Estende il modello custom-group originale: i campi `_*` guidano solo la prima
 * colonna (pulsantino espandi/comprimi + indentazione), mentre `aggregatedRows`
 * mantiene lo stesso shape per qualunque livello (customGroup/carSegment/
 * carClass/ACRISS), così le colonne dati restano invariate rispetto a LEONARDO.
 */
type DrillRow = IARRowsPerGroup & {
  _level?: number;
  _expandable?: boolean;
  _expanded?: boolean;
  _nodeId?: string;
  /** Drill-down del livello non ancora caricato: l'espansione lo carica dal BE. */
  _needsLoad?: boolean;
  /** Caricamento del drill-down in corso. */
  _loading?: boolean;
};

const dateFormats = {
  tableGroupHeader: "DD/MM/YYYY HH:mm",
  tableSimpleHeader: "DD/MM/YYYY",
};

/**
 * Normalizza gli `aggregatedRows` di un nodo (customGroup/carSegment/carClass/ACRISS)
 * preservando TUTTI i giorni passati e futuri (CR LPP-20500 RF-01/RF-14): le colonne
 * leggono per `previewDate` (passati/futuri) e per `dayType` (oggi), non più per indice
 * posizionale. Garantisce comunque la presenza degli slot START_TODAY/CURRENT_TODAY
 * (placeholder se mancanti, per i livelli drill-down degeneri).
 */
const normalizeAggregatedRows = (
  rows: IARAggregatedRow[] | undefined
): IARAggregatedRow[] => {
  const source = rows ?? [];

  const past = source.filter((row) => row.dayType === "PAST");
  const startToday =
    source.find((row) => row.dayType === "START_TODAY") ?? { dayType: "START_TODAY" as const };
  const currentToday =
    source.find((row) => row.dayType === "CURRENT_TODAY") ?? { dayType: "CURRENT_TODAY" as const };
  const future = source.filter((row) => row.dayType === "FUTURE");

  return [...past, startToday, currentToday, ...future];
};

export const SingleLocationTable = (props: Props) => {
  const [t, i18n] = useTranslation(["operation"]);

  // CR LPP-20500: default true per retrocompatibilità (giorno corrente sempre nel range).
  const todayInRange = props.todayInRange ?? true;

  const [isAccordionExpanded, setIsAaccordionExpanded] = useState(
    props.defaultExpanded ?? true
  );

  const [totalPast, setTotalPast] = useState<IARAggregatedRow | undefined>();
  const [totalStartToday, setTotalStartToday] = useState<
    IARAggregatedRow | undefined
  >();
  const [totalCurrentToday, setTotalCurrentToday] = useState<
    IARAggregatedRow | undefined
  >();

  /**
   * Insieme dei nodi attualmente espansi (chiave gerarchica `_nodeId`).
   * Default: tutto chiuso (RF-08). Al click si espande un livello alla volta.
   */
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // CR LPP-20500 (drilldown scalabile): drill-down caricato a richiesta alla prima espansione.
  const drill = useLazyDrillDown(props.onLoadDrillDown, props.data);

  const handleExpandClick = (row: DrillRow | undefined) => {
    const nodeId = row?._nodeId;
    if (!nodeId) return;
    if (row?._needsLoad) {
      drill.load().then((loaded) => {
        if (loaded) {
          setExpandedNodes((prev) => new Set(prev).add(nodeId));
        }
      });
      return;
    }
    toggleNode(nodeId);
  };

  const formatUtilization = (value: any) => {
    const num = Number((value * 100).toFixed(2));
    return num.toLocaleString();
  }

  const totalRowsData = useMemo<IARRowsPerGroup[]>(
    () => [
      {
        group: `${t("availabilityReport.TOTALE")} - ${
          props.data.location.locationName
        }`,
        groupOrdering: 2000,
        isTotal: true,
        aggregatedRows: props.data.totalRowsByLocation,
      },
      {
        group: "",
        groupOrdering: 2001,
        aggregatedRows: [
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "PAST")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "PAST",
                previewDate: element.previewDate,
                availability: t("availabilityReport.MAINTENANCE"),
                extraField: element.maintenance,
                startReservationNeed: t("availabilityReport.DEFLEET"),
                endReservationSupply: element.defleet,
              })
            ),
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.MAINTENANCE"),
            startReservationNeed: totalStartToday?.maintenance,
            endReservationSupply: t("availabilityReport.DEFLEET"),
            balance: totalStartToday?.defleet,
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.MAINTENANCE"),
            startReservationNeed: totalCurrentToday?.maintenance,
            endReservationSupply: t("availabilityReport.DEFLEET"),
            balance: totalCurrentToday?.defleet,
          },
        ],
      },
      {
        group: "",
        groupOrdering: 2002,
        aggregatedRows: [
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "PAST")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "PAST",
                previewDate: element.previewDate,
                availability: t("availabilityReport.trasfInEntrata"),
                extraField: element.incomingTranfers,
                startReservationNeed: t("availabilityReport.trasfInUscita"),
                endReservationSupply: element.outgoingTransfers,
              })
            ),
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.trasfInEntrata"),
            startReservationNeed: totalStartToday?.incomingTranfers,
            endReservationSupply: t("availabilityReport.trasfInUscita"),
            balance: totalStartToday?.outgoingTransfers,
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.trasfInEntrata"),
            startReservationNeed: totalCurrentToday?.incomingTranfers,
            endReservationSupply: t("availabilityReport.trasfInUscita"),
            balance: totalCurrentToday?.outgoingTransfers,
          },
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "FUTURE")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "FUTURE",
                endReservationSupply: t("availabilityReport.PREVRIENTRIREPL"),
                balance: element.replacementReturns,
                previewDate: element.previewDate,
              })
            ),
        ],
      },
      {
        group: "",
        groupOrdering: 2003,
        // CR LPP-20500 RF-09: RIENTRI REPL / UTILIZZO (ieri / oggi inizio / oggi reale) sulla stessa riga
        // dell'utilizzo prospettico sui giorni futuri (nessuna riga vuota dedicata).
        aggregatedRows: [
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "PAST")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "PAST",
                previewDate: element.previewDate,
                availability: t("availabilityReport.RIENTRIREPL"),
                extraField: element.replacementReturns,
                startReservationNeed: t("availabilityReport.UTILIZZO"),
                endReservationSupply:
                  element.utilization != null
                    ? `${formatUtilization(element.utilization)} %`
                    : t("availabilityReport.notAvailableAchronym"),
              })
            ),
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.PREVRIENTRIREPL"),
            startReservationNeed: totalStartToday?.replacementReturns,
            endReservationSupply: t("availabilityReport.UTILIZZO"),
            balance:
              totalStartToday && totalStartToday.utilization
                ? `${formatUtilization(totalStartToday?.utilization)} %`
                : t("availabilityReport.notAvailableAchronym"),
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.RIENTRIREPL"),
            startReservationNeed: totalCurrentToday?.replacementReturns,
            endReservationSupply: t("availabilityReport.UTILIZZO"),
            balance:
              totalCurrentToday && totalCurrentToday.utilization
                ? `${formatUtilization(totalCurrentToday?.utilization)} %`
                : t("availabilityReport.notAvailableAchronym"),
          },
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "FUTURE")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "FUTURE",
                endReservationSupply: t("availabilityReport.UTILIZZO"),
                balance:
                  element.utilization != null
                    ? `${formatUtilization(element.utilization)} %`
                    : t("availabilityReport.notAvailableAchronym"),
                previewDate: element.previewDate,
              })
            ),
        ],
      },
      {
        group: "",
        groupOrdering: 2004,
        // CR LPP-20500 RF-10/RF-11 + v1.5: Tot in flotta / Tot on rent (ieri / oggi inizio / oggi reale / futuri)
        aggregatedRows: [
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "PAST")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "PAST",
                previewDate: element.previewDate,
                availability: t("availabilityReport.totInFleet", "Tot in flotta"),
                extraField: element.totalInFleet,
                startReservationNeed: t("availabilityReport.totOnRent", "Tot on rent"),
                endReservationSupply: element.totalOnRent,
              })
            ),
          {
            dayType: "START_TODAY",
            availability: t("availabilityReport.totInFleet", "Tot in flotta"),
            startReservationNeed: totalStartToday?.totalInFleet,
            endReservationSupply: t("availabilityReport.totOnRent", "Tot on rent"),
            balance: totalStartToday?.totalOnRent,
          },
          {
            dayType: "CURRENT_TODAY",
            availability: t("availabilityReport.totInFleet", "Tot in flotta"),
            startReservationNeed: totalCurrentToday?.totalInFleet,
            endReservationSupply: t("availabilityReport.totOnRent", "Tot on rent"),
            balance: totalCurrentToday?.totalOnRent,
          },
          // CR LPP-20500 v1.5 (precisazioni BU): KPI proiettati sui giorni futuri, stessa riga del
          // giorno corrente. Il giorno futuro ha 3 colonne (RES/RIENTRI/BAL): label+valore nella stessa
          // cella — "Tot in flotta: N" in RES, "Tot on rent: N" in RIENTRI, BAL vuota.
          ...props.data.totalRowsByLocation
            .filter((row) => row.dayType === "FUTURE")
            .map(
              (element): IARAggregatedRow => ({
                dayType: "FUTURE",
                previewDate: element.previewDate,
                startReservationNeed: `${t("availabilityReport.totInFleet", "Tot in flotta")}: ${element.totalInFleet ?? ""}`,
                endReservationSupply: `${t("availabilityReport.totOnRent", "Tot on rent")}: ${element.totalOnRent ?? ""}`,
              })
            ),
        ],
      },
    ],
    [totalPast, totalStartToday, totalCurrentToday, i18n.language]
  );

  const getDayName = (day: number) => {
    switch (day) {
      case 0:
        return t("availabilityReport.weekDays.sunday");

      case 1:
        return t("availabilityReport.weekDays.monday");

      case 2:
        return t("availabilityReport.weekDays.tuesday");

      case 3:
        return t("availabilityReport.weekDays.wednesday");

      case 4:
        return t("availabilityReport.weekDays.thursday");

      case 5:
        return t("availabilityReport.weekDays.friday");

      case 6:
        return t("availabilityReport.weekDays.saturday");

      default:
        return t("availabilityReport.weekDays.monday");
    }
  };

  const futureDaysColumnDefsData = useMemo<(string | undefined)[]>(
    () =>
      props.data.rowsPerGroup[0].aggregatedRows
        .filter((row) => row.dayType === "FUTURE")
        .map((row) => row.previewDate),
    [props.data]
  );

  /**
   * CR LPP-20500 RF-01/RF-14 — giorni passati selezionati (startDate nel passato).
   * Una colonna per ogni giorno passato (dayType PAST), come per i futuri, ordinati
   * ascendenti: l'ultimo è "ieri". Le colonne leggono per previewDate (non più per
   * indice posizionale, che si romperebbe con più giorni passati).
   */
  const pastDaysColumnDefsData = useMemo<(string | undefined)[]>(
    () =>
      props.data.rowsPerGroup[0].aggregatedRows
        .filter((row) => row.dayType === "PAST")
        .map((row) => row.previewDate)
        .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()),
    [props.data]
  );

  /** Totali per location del giorno passato, indicizzati per previewDate (per header rientrati/RA/void/fail). */
  const totalPastByDate = useMemo<Map<string | undefined, IARAggregatedRow>>(() => {
    const map = new Map<string | undefined, IARAggregatedRow>();
    props.data.totalRowsByLocation
      .filter((row) => row.dayType === "PAST")
      .forEach((row) => map.set(row.previewDate, row));
    return map;
  }, [props.data]);

  const columnCellRenderer = (
    params: ICellRendererParams<IARRowsPerGroup>,
    value: string | number | undefined
  ) => {
    if (params.data?.isTotal || typeof value === "string") {
      return (
        <Mui.Tooltip title={value || ""}>
          <Mui.Typography variant="h6" my={"auto"}>
            {value}
          </Mui.Typography>
        </Mui.Tooltip>
      );
    }

    return value;
  };

  /**
   * CR LPP-20500 RF-01/RF-14 — costruisce il gruppo colonne di un giorno passato,
   * con lo stesso dettaglio di "Ieri fine giornata" (Rientrati/RA Emessi/Void/Fail +
   * AVAIL/RES/RIENTRI). L'ultimo giorno passato (ieri) usa la label "Ieri fine giornata",
   * gli altri "<gg mese> fine giornata" in lingua corrente. Le celle leggono per previewDate.
   */
  const buildPastDayColGroup = (element: string | undefined): ColGroupDef => {
    const pastRow = totalPastByDate.get(element);
    const isYesterday =
      pastDaysColumnDefsData.length > 0 &&
      element === pastDaysColumnDefsData[pastDaysColumnDefsData.length - 1];
    const groupHeader = isYesterday
      ? `${t("availabilityReport.ieriFineGiornata")} ${dayjs(element)
          .set("hour", 23)
          .set("minute", 59)
          .format(dateFormats.tableGroupHeader)}`
      : `${dayjs(element).format("D MMMM")} ${t("availabilityReport.fineGiornata", "fine giornata")}`;
    const cellClassBlack = (params: CellClassParams<IARRowsPerGroup>) => {
      let ret = "ag-grid-cell-background-black ag-grid-align-center";
      if (params.data?.isTotal) ret += " ag-grid-cell-totals ";
      return ret;
    };
    const findRow = (params: ICellRendererParams<IARRowsPerGroup>) =>
      params.data?.aggregatedRows?.find(
        (row) => row.previewDate === element && row.dayType === "PAST"
      );
    return {
      headerName: groupHeader,
      headerClass: "ag-grid-cell-background-black",
      children: [
        {
          headerName: `${t("availabilityReport.rientratiIeri")} ${
            pastRow?.effectiveReturns || 0
          }`,
          headerClass: "ag-grid-cell-background-black",
          children: [
            {
              headerName: `${t("availabilityReport.raEmessiIeri")} ${
                pastRow?.issuedRa || 0
              }`,
              headerClass: "ag-grid-cell-background-black",
              children: [
                {
                  headerName: t("availabilityReport.AvailEndDay"),
                  minWidth: 290,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: cellClassBlack,
                  cellRenderer: (params: ICellRendererParams<IARRowsPerGroup>) =>
                    columnCellRenderer(params, findRow(params)?.availability),
                },
                {
                  headerName: "",
                  minWidth: 50,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: cellClassBlack,
                  cellRenderer: (params: ICellRendererParams<IARRowsPerGroup>) =>
                    columnCellRenderer(params, findRow(params)?.extraField),
                },
              ],
            },
          ],
        },
        {
          headerName: `${t("availabilityReport.voidIeri")} ${
            pastRow?.voidCount || 0
          }`,
          headerClass: "ag-grid-cell-background-black",
          children: [
            {
              headerName: `${t("availabilityReport.failIeri")} ${
                pastRow?.failedCount || 0
              }`,
              headerClass: "ag-grid-cell-background-black",
              children: [
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 175,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: cellClassBlack,
                  cellRenderer: (params: ICellRendererParams<IARRowsPerGroup>) =>
                    columnCellRenderer(params, findRow(params)?.startReservationNeed),
                },
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 190,
                  headerClass: "ag-grid-cell-background-black",
                  cellClass: cellClassBlack,
                  cellRenderer: (params: ICellRendererParams<IARRowsPerGroup>) =>
                    columnCellRenderer(params, findRow(params)?.endReservationSupply),
                },
              ],
            },
          ],
        },
      ],
    };
  };

  const allColumnDefs: ColGroupDef[] = [
    {
      headerName: "",
      children: [
        {
          headerName: "",
          minWidth: 280,
          field: nameof<IARRowsPerGroup>("group"),
          pinned: "left",
          cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
            if (params.data?.isTotal) {
              return " ag-grid-cell-totals ";
            }
          },
          cellRenderer: (params: ICellRendererParams<DrillRow>) => {
            const row = params.data;
            const level = row?._level ?? 0;
            const expandable = row?._expandable ?? false;
            const expanded = row?._expanded ?? false;
            return (
              <Mui.Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ pl: level * 2 }}
              >
                {expandable ? (
                  <Mui.IconButton
                    size="small"
                    onClick={() => handleExpandClick(row)}
                    disabled={row?._loading}
                    sx={{ p: 0 }}
                  >
                    {row?._loading ? (
                      <Mui.CircularProgress size={16} />
                    ) : expanded ? (
                      <MuiIcons.Remove fontSize="small" />
                    ) : (
                      <MuiIcons.Add fontSize="small" color="error" />
                    )}
                  </Mui.IconButton>
                ) : (
                  level > 0 && <Mui.Box sx={{ width: 24 }} />
                )}
                <Mui.Tooltip title={params.value}>
                  <Mui.Typography
                    variant="h6"
                    lineHeight={"inherit"}
                    sx={level > 0 ? { fontWeight: 400, fontSize: "0.9em" } : undefined}
                  >
                    {params.value}
                  </Mui.Typography>
                </Mui.Tooltip>
              </Mui.Stack>
            );
          },
        },
      ],
    },
    ...pastDaysColumnDefsData.map((element) => buildPastDayColGroup(element)),
    {
      headerName: `${t("availabilityReport.oggiInizioGiornata")} - ${dayjs()
        .set("hour", 7)
        .set("minute", 0)
        .format(dateFormats.tableGroupHeader)}`,
      groupId: "startTodayGroup",
      headerClass: "ag-grid-cell-background-yellow",
      children: [
        {
          headerName: "",
          headerClass: "ag-grid-cell-background-yellow",
          children: [
            {
              headerName: "",
              headerClass: "ag-grid-cell-background-yellow",
              children: [
                {
                  headerName: t("availabilityReport.availStartDay"),
                  minWidth: 270,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "START_TODAY"
                      )?.availability
                    ),
                },
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 90,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "START_TODAY"
                      )?.startReservationNeed
                    ),
                },
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 190,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "START_TODAY"
                      )?.endReservationSupply
                    ),
                },
                {
                  headerName: t("availabilityReport.BAL"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-yellow",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-yellow ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                    const balance = params.data?.aggregatedRows?.find(
                      (row) => row.dayType === "START_TODAY"
                    )?.balance as number;
                    if ((balance && balance < 0) || balance === 0) {
                      return {
                        color: "red",
                      };
                    }
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "START_TODAY"
                      )?.balance
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      headerName: `${t("availabilityReport.oggiTempoReale")} - ${dayjs().format(
        dateFormats.tableGroupHeader
      )}`,
      groupId: "currentTodayGroup",
      headerClass: "ag-grid-cell-background-green",
      children: [
        {
          headerName: `${t("availabilityReport.rientratiOggi")} ${
            totalCurrentToday?.effectiveReturns || 0
          }`,
          headerClass: "ag-grid-cell-background-green",
          children: [
            {
              headerName: `${t("availabilityReport.raEmessiOggi")} ${
                totalCurrentToday?.issuedRa || 0
              }`,
              headerClass: "ag-grid-cell-background-green",
              children: [
                {
                  headerName: "AVAIL NOW",
                  minWidth: 270,
                  colId: "currentTodayAvail",
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "CURRENT_TODAY"
                      )?.availability
                    ),
                },
                {
                  headerName: t("availabilityReport.RES"),
                  minWidth: 90,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "CURRENT_TODAY"
                      )?.startReservationNeed
                    ),
                },
              ],
            },
          ],
        },
        {
          headerName: `${t("availabilityReport.voidOggi")} ${
            totalCurrentToday?.voidCount || 0
          }`,
          headerClass: "ag-grid-cell-background-green",
          children: [
            {
              headerName: `${t("availabilityReport.failOggi")} ${
                totalCurrentToday?.failedCount || 0
              }`,
              headerClass: "ag-grid-cell-background-green",
              children: [
                {
                  headerName: t("availabilityReport.RIENTRI"),
                  minWidth: 190,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "CURRENT_TODAY"
                      )?.endReservationSupply
                    ),
                },
                {
                  headerName: t("availabilityReport.BAL"),
                  minWidth: 100,
                  headerClass: "ag-grid-cell-background-green",
                  cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                    let ret = "ag-grid-cell-background-green ag-grid-align-center";

                    if (params.data?.isTotal) {
                      ret += " ag-grid-cell-totals ";
                    }

                    return ret;
                  },
                  cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                    const balance = params.data?.aggregatedRows?.find(
                      (row) => row.dayType === "CURRENT_TODAY"
                    )?.balance as number;
                    if ((balance && balance < 0) || balance === 0) {
                      return {
                        color: "red",
                      };
                    }
                  },
                  cellRenderer: (
                    params: ICellRendererParams<IARRowsPerGroup>
                  ) =>
                    columnCellRenderer(
                      params,
                      params.data?.aggregatedRows?.find(
                        (row) => row.dayType === "CURRENT_TODAY"
                      )?.balance
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    ...futureDaysColumnDefsData.map(
      (element): ColGroupDef => ({
        headerName: "",
        children: [
          {
            headerGroupComponent: () => {
              const day = dayjs(element).day();
              let bgColor = "transparent";
              let textColor = "black";

              if (day === 6) {
                bgColor = "#00A0DF";
                textColor = "white";
              } else if (day === 0) {
                bgColor = "#F418A1";
                textColor = "white";
              }

              return (
                <Mui.Box
                  padding={2}
                  bgcolor={bgColor}
                  width={"100%"}
                  textAlign={"center"}
                  color={textColor}
                >
                  {getDayName(day)}
                </Mui.Box>
              );
            },
            headerClass: "ag-grid-cell-right-border ag-grid-cell-no-padding",
            children: [
              {
                headerName: dayjs(element).format(
                  dateFormats.tableSimpleHeader
                ),
                headerGroupComponent: () => (
                  <Mui.Box textAlign={"center"} width={"100%"}>
                    {dayjs(element).format(dateFormats.tableSimpleHeader)}
                  </Mui.Box>
                ),
                headerClass: "ag-grid-cell-right-border",
                children: [
                  {
                    headerName: t("availabilityReport.RES"),
                    minWidth: 175,
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ";
                      }

                      return ret;
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.startReservationNeed
                      ),
                  },
                  {
                    headerName: t("availabilityReport.RIENTRI"),
                    minWidth: 190,
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ";
                      }
                      
                      return ret;
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.endReservationSupply
                      ),
                  },
                  {
                    headerName: t("availabilityReport.BAL"),
                    minWidth: 120,
                    headerClass: "ag-grid-cell-right-border",
                    cellClass: (params: CellClassParams<IARRowsPerGroup>) => {
                      let ret = "ag-grid-align-center";

                      if (params.data?.isTotal) {
                        ret += " ag-grid-cell-totals ag-grid-cell-right-border ";
                      }
                      
                      return ret;
                    },
                    cellStyle: (params: CellClassParams<IARRowsPerGroup>) => {
                      const balance = params.data?.aggregatedRows.find(
                        (row) => row.previewDate === element
                      )?.balance as number;
                      if ((balance && balance < 0) || balance === 0) {
                        return {
                          color: "red",
                          borderRight: "solid 1px black",
                        };
                      }

                      return {
                        color: "inherit",
                        borderRight: "solid 1px black",
                      };
                    },
                    cellRenderer: (
                      params: ICellRendererParams<IARRowsPerGroup>
                    ) =>
                      columnCellRenderer(
                        params,
                        params.data?.aggregatedRows.find(
                          (row) => row.previewDate === element
                        )?.balance
                      ),
                  },
                ],
              },
            ],
          },
        ],
      })
    ),
  ];

  /**
   * CR LPP-20500 — quando il giorno corrente non fa parte del range ricercato
   * (ricerca interamente passata o futura) si nascondono i gruppi colonna "oggi
   * inizio giornata" e "oggi tempo reale": resta solo il range richiesto.
   */
  const columnDefs: ColGroupDef[] = todayInRange
    ? allColumnDefs
    : allColumnDefs.filter(
        (cd) =>
          cd.groupId !== "startTodayGroup" && cd.groupId !== "currentTodayGroup"
      );

  // LPP-20500 (perf) — larghezze fisse per tipo di colonna (vedi availabilityColumnWidths.ts).
  const gridColumnDefs = useMemo<ColGroupDef[]>(
    () => applyFixedColumnWidths(columnDefs, i18n.language) as ColGroupDef[],
    [columnDefs, i18n.language]
  );

  useEffect(() => {
    const past = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "PAST"
    );
    const startToday = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "START_TODAY"
    );
    const currentToday = props.data.totalRowsByLocation.find(
      (element) => element.dayType === "CURRENT_TODAY"
    );

    setTotalPast(past);
    setTotalStartToday(startToday);
    setTotalCurrentToday(currentToday);
  }, [props.data]);

  /**
   * Appiattisce la gerarchia custom group → carSegment → carClass → ACRISS in
   * una lista di righe per la griglia, includendo solo i livelli espansi.
   * Le righe figlie usano lo stesso shape `aggregatedRows` del custom group,
   * quindi le colonne dati le renderizzano senza alcuna modifica.
   */
  const buildDrillRows = (groups: IARRowsPerGroup[]): DrillRow[] => {
    const out: DrillRow[] = [];

    groups.forEach((sourceGroup) => {
      // CR LPP-20500 (drilldown scalabile): se il drill-down è stato caricato a richiesta lo innesto
      // sul gruppo; se non è ancora caricato (carSegments assente) il gruppo resta espandibile.
      const group = drill.resolveGroup(sourceGroup);
      const groupId = `g:${group.group}`;
      const segments = group.carSegments ?? [];
      const groupNeedsLoad = drill.needsLoad(group);
      const groupExpandable = segments.length > 0 || groupNeedsLoad;
      const groupExpanded = expandedNodes.has(groupId);

      out.push({
        ...group,
        _level: 0,
        _expandable: groupExpandable,
        _expanded: groupExpanded,
        _nodeId: groupId,
        _needsLoad: groupNeedsLoad,
        _loading: groupNeedsLoad && drill.loading,
      });

      /*
       * CR LPP-20500 RF-06 — split automatico/manuale.
       * Per i custom group target (es. "Comp/Int/Standard") il BE popola
       * `transmissionGroups` con le righe riassuntive (A)/(M). Vanno mostrate
       * SEMPRE, allo stesso livello del custom group, senza pulsante di espansione.
       * FIX ordinamento: i car segment espansi vanno mostrati DIRETTAMENTE sotto il
       * custom group; le righe (A)/(M) scorrono quindi SOTTO l'eventuale drill-down
       * (prima venivano renderizzate subito dopo il gruppo, lasciando i segment in coda).
       */
      const transmissions = group.transmissionGroups ?? [];

      const pushTransmissions = () =>
        transmissions.forEach((tg) => {
          out.push({
            group: tg.displayName,
            groupOrdering: group.groupOrdering,
            aggregatedRows: normalizeAggregatedRows(tg.aggregatedRows),
            _level: 0,
            _expandable: false,
            _nodeId: `${groupId}|t:${tg.transmission}`,
          });
        });

      if (!groupExpanded) {
        // Gruppo chiuso: le (A)/(M) restano subito sotto il custom group.
        pushTransmissions();
        return;
      }

      segments.forEach((seg) => {
        const segId = `${groupId}|s:${seg.carSegment}`;
        const classes = seg.carClasses ?? [];
        const segExpandable = classes.length > 0;
        const segExpanded = expandedNodes.has(segId);

        out.push({
          group: seg.carSegment,
          groupOrdering: group.groupOrdering,
          aggregatedRows: normalizeAggregatedRows(seg.aggregatedRows),
          _level: 1,
          _expandable: segExpandable,
          _expanded: segExpanded,
          _nodeId: segId,
        });

        if (!segExpanded) return;

        classes.forEach((cls) => {
          const clsId = `${segId}|c:${cls.carClass}`;
          const acrisses = cls.acrissDetails ?? [];
          const clsExpandable = acrisses.length > 0;
          const clsExpanded = expandedNodes.has(clsId);

          out.push({
            group: cls.carClass,
            groupOrdering: group.groupOrdering,
            aggregatedRows: normalizeAggregatedRows(cls.aggregatedRows),
            _level: 2,
            _expandable: clsExpandable,
            _expanded: clsExpanded,
            _nodeId: clsId,
          });

          if (!clsExpanded) return;

          acrisses.forEach((acr) => {
            out.push({
              group: acr.acriss,
              groupOrdering: group.groupOrdering,
              aggregatedRows: normalizeAggregatedRows(acr.rows),
              _level: 3,
              _expandable: false,
              _nodeId: `${clsId}|a:${acr.acriss}`,
            });
          });
        });
      });

      // (A)/(M) dopo l'eventuale drill-down dei car segment espansi (vedi FIX ordinamento sopra).
      pushTransmissions();
    });

    return out;
  };

  const allData = useMemo(() => {
    return [...buildDrillRows(props.data.rowsPerGroup), ...totalRowsData];
  }, [
    props.data,
    expandedNodes,
    drill.segmentsByGroup,
    drill.loading,
    totalPast,
    totalCurrentToday,
    totalStartToday,
    i18n.language,
  ]);

  const gridRef = useRef<AgGridReact>(null);

  /**
   * CR LPP-20500 RF-01/RF-14 — ad ogni (ri)ricerca posiziona la griglia sulla colonna
   * del giorno corrente (CURRENT_TODAY), lasciando l'operatore libero di scorrere a
   * sinistra (passato) o a destra (futuro) con la barra orizzontale.
   */
  const scrollToCurrentToday = () => {
    if (!todayInRange) return;
    gridRef.current?.api?.ensureColumnVisible("currentTodayAvail", "middle");
  };

  useEffect(() => {
    const id = setTimeout(scrollToCurrentToday, 0);
    return () => clearTimeout(id);
    // CR LPP-20500: lo scroll su "oggi" deve avvenire solo a (ri)ricerca (cambio dati),
    // NON quando si espande/comprime il drill-down (che cambia allData via expandedNodes),
    // altrimenti la griglia salta a sinistra perdendo la posizione scelta dall'operatore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  /**
   * LPP-20500 (perf) — l'auto-size al contenuto è limitato alla sola colonna pinned (nomi gruppo/drill-down,
   * più lunghi con l'indentazione). Le colonne dati hanno larghezza fissa: misurarle tutte (`autoSizeAllColumns`)
   * costava ~50% del tempo CPU con 180 giorni.
   */
  const autoSizeGroupColumn = () =>
    gridRef.current?.columnApi?.autoSizeColumns([GROUP_COL_ID]);

  /*
   * Le larghezze fisse sono il punto di partenza; qui si raffinano al contenuto reale (etichette tradotte, valori)
   * misurando solo poche colonne visibili per tipo. `canFitWidths` evita di misurare prima che le righe siano
   * renderizzate (header senza celle).
   */
  const measuredKinds = useRef(newFitState());
  const canFitWidths = useRef(false);
  const fitVisibleColumns = () => {
    const api = gridRef.current?.columnApi;
    if (api && canFitWidths.current) fitVisibleColumnTypes(api, measuredKinds.current);
  };
  const refitWidths = () => {
    resetFitState(measuredKinds.current);
    autoSizeGroupColumn();
    fitVisibleColumns();
  };

  useEffect(() => {
    const id = setTimeout(refitWidths, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, expandedNodes, i18n.language]);

  return (
    <Mui.Accordion
      expanded={isAccordionExpanded}
      // LPP-20500 (perf): griglia montata solo ad accordion aperto (smontata alla chiusura).
      TransitionProps={{ unmountOnExit: true }}
    >
      <Mui.AccordionSummary
        expandIcon={
          isAccordionExpanded ? (
            <MuiIcons.Remove />
          ) : (
            <MuiIcons.Add color={"error"} />
          )
        }
        onClick={() => setIsAaccordionExpanded(!isAccordionExpanded)}
      >
        <Mui.Stack direction={"row"} spacing={1} alignItems={"center"}>
          <Mui.Typography color={"#FF00FF"} variant="bodyLarge">
            {props.data.location.region}
          </Mui.Typography>
          <span>{` - `}</span>
          <Mui.Typography>{props.data.location.locationName}{` (${props.data.location.locationType === "LMS" ? "DMS" : "Piazzale"})`}</Mui.Typography>
        </Mui.Stack>
      </Mui.AccordionSummary>

      <Mui.AccordionDetails>
        <AgGridCustomTable
          gridRef={gridRef}
          columnDefs={gridColumnDefs}
          pagination={false}
          rowData={allData}
          rowHeight={32}
          // LPP-20500 (perf): virtualizzazione colonne attiva (prima `suppressColumnVirtualisation`):
          // si renderizzano solo le colonne visibili, non tutti i 180 giorni.
          onGridReady={(event: GridReadyEvent) => {
            canFitWidths.current = false;
            resetFitState(measuredKinds.current);
            event.columnApi.autoSizeColumns([GROUP_COL_ID]);
            if (todayInRange)
              event.api.ensureColumnVisible("currentTodayAvail", "middle");
          }}
          onFirstDataRendered={(event: FirstDataRenderedEvent) => {
            // auto-size della colonna pinned e delle colonne dati visibili DOPO il render delle righe.
            event.columnApi.autoSizeColumns([GROUP_COL_ID]);
            canFitWidths.current = true;
            fitVisibleColumns();
          }}
          // nuove colonne entrate nel viewport con lo scroll: si misurano solo i tipi non ancora misurati.
          onBodyScrollEnd={fitVisibleColumns}
        />
      </Mui.AccordionDetails>
    </Mui.Accordion>
  );
};
